/**
 * useNoOpReconciliation - reconciles two distinct, fail-open derived writes
 * to `is_selected` that `ProposalSelectionContext` needs on load and as the
 * live CRM store settles:
 *
 * 1. Auto-deselecting fully no-op actions (`noOpActionIds`), so a proposal
 *    that loads with no-ops already checked doesn't leave them selected.
 * 2. Reconciling a still-checked action carrying a server `missing_entity`
 *    verdict, so the persisted `is_selected` set matches the applicable set
 *    approval sends and the approve endpoint doesn't reject the mismatch as
 *    409 STALE_SELECTION.
 *
 * They are grouped here because both exist purely to keep the SERVER's
 * persisted `is_selected` in agreement with the CLIENT's derived applicable
 * set — neither one ever toggles a checkbox as a "healing" decision, and
 * both rely on signature-based refs to avoid redundant PATCHes when an
 * effect re-runs with unchanged inputs.
 */

import { type RefObject, useEffect, useRef } from 'react';

import type { ProposedAction } from '../core/types';

/** Order-insensitive equality of two action-id lists. */
export function sameIdSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const set = new Set(a);
  return b.every(id => set.has(id));
}

interface UseNoOpReconciliationParams {
  /**
   * Selection-aware no-op set — see `ProposalSelectionContext`'s
   * `noOpActionIds` for how it's derived.
   */
  noOpActionIds: number[];
  proposalId: number;
  /** Raw checked ids, from `useActionSelection`. */
  selectedActionIds: number[];
  /** Map from action ID to the action object, for server-verdict lookups. */
  actionsById: Map<number, ProposedAction>;
  deselectActionsSelection: (actionIds: number[]) => void;
  scheduleSync: (getIds: () => number[], onError?: () => void) => void;
  /** Ref mirroring the latest `selectedActionIds`, for rollback-on-error. */
  selectedIdsRef: RefObject<number[]>;
  /** Ref mirroring the latest applicable (checked AND valid) ids. */
  applicableIdsRef: RefObject<number[]>;
}

export function useNoOpReconciliation({
  noOpActionIds,
  proposalId,
  selectedActionIds,
  actionsById,
  deselectActionsSelection,
  scheduleSync,
  selectedIdsRef,
  applicableIdsRef,
}: UseNoOpReconciliationParams): void {
  // Auto-deselect fully no-op actions once per proposal (and per no-op
  // signature), so a proposal refresh that echoes the server's already-false
  // is_selected doesn't retrigger the sync. `noOpActionIds` is now
  // live-first, server-second (see `noOpActions.ts`'s `isFullyNoOpAction`),
  // so this signature can change as the live store hydrates or a record
  // changes, not just on proposal refresh. deselectActionsSelection is
  // idempotent (guards on current selection), so this is safe to call even
  // if the effect were to re-run.
  const autoDeselectedRef = useRef<string>('');
  useEffect(() => {
    if (noOpActionIds.length === 0) {
      // A live record can drift back to matching a no-op it previously matched
      // (e.g. a user's edit is reverted). Clear the memoized signature when the
      // no-op set empties so the NEXT detection of that same signature runs the
      // deselect again, instead of being skipped as already-handled — which
      // would hide the action from `visibleActions` without deselecting it.
      autoDeselectedRef.current = '';
      return;
    }
    const signature = `${proposalId}:${[...noOpActionIds].sort((a, b) => a - b).join(',')}`;
    // Skip only when this exact no-op signature was already handled AND no no-op
    // id is currently selected. A proposal refresh that re-selects an
    // already-deselected no-op (or a failed deselect PATCH the server never
    // applied) leaves the signature unchanged while a no-op id is checked again
    // — a signature-only guard would skip it, leaving the action checked but
    // hidden, so the applicable set approval sends excludes it while persisted
    // is_selected includes it (409 STALE_SELECTION). Re-running whenever a no-op
    // id is currently selected closes that gap; it terminates because the
    // deselect below drops those ids from the selection, after which this guard
    // short-circuits on the next run.
    if (
      autoDeselectedRef.current === signature &&
      !noOpActionIds.some(id => selectedIdsRef.current.includes(id))
    ) {
      return;
    }
    autoDeselectedRef.current = signature;
    // Only sync when the deselect actually changes something. When a proposal
    // loads with its no-op actions already deselected (the server echoes
    // is_selected=false), deselectActionsSelection is a no-op, so a PATCH would
    // just be avoidable write traffic on every load. Check the current
    // selection BEFORE deselecting: if no no-op id is selected, there's nothing
    // to persist.
    const needsSync = noOpActionIds.some(id =>
      selectedIdsRef.current.includes(id)
    );
    deselectActionsSelection(noOpActionIds);
    if (needsSync) {
      scheduleSync(
        () => applicableIdsRef.current,
        () => {
          // The auto-deselect PATCH failed. Clear the memoized signature so the
          // refresh the failed sync triggers re-runs this effect and retries —
          // otherwise the server's is_selected set stays stale and approval is
          // stuck at 409 STALE_SELECTION until an unrelated selection change.
          autoDeselectedRef.current = '';
        }
      );
    }
  }, [
    noOpActionIds,
    proposalId,
    deselectActionsSelection,
    scheduleSync,
    selectedIdsRef,
    applicableIdsRef,
  ]);

  // Reconcile a server `missing_entity` verdict on initial load. Such a verdict
  // excludes a still-checked action from the applicable set (`applicableActionIds`)
  // WITHOUT deselecting it — the checkbox stays checked so it returns the moment
  // its prerequisite is created. That exclusion is authoritative and known at
  // mount (a server verdict, unlike the client-projected validity below, is not
  // transient while the CRM store loads). So a proposal that loads with such an
  // action already `is_selected=true` on the server has a persisted `is_selected`
  // set that INCLUDES the action while the applicable set approval sends EXCLUDES
  // it — and the approve endpoint rejects that mismatch as 409 STALE_SELECTION.
  // The passive-sync effect below can't catch this: its baseline is initialised
  // to the mount applicable set, so it sees no change on load. Persist the
  // applicable set once when a checked action carries a `missing_entity` verdict.
  // Keyed by signature so a proposal refresh echoing the same state doesn't re-PATCH.
  //
  // This is a SECOND, distinct derived write to `is_selected` — not the same one
  // as the `autoDeselectedRef` no-op-deselect effect above. The one referenced by
  // this file's "healing never writes to is_selected" framing is the no-op
  // auto-deselect PATCH; this reconciliation PATCH is a different, independent
  // write that exists purely to prevent a checked `missing_entity` action from
  // triggering 409 STALE_SELECTION on approve. It does not violate the "healing
  // never writes is_selected" policy in spirit: it never toggles a checkbox or
  // acts on the verdict as a healing decision — it only syncs the persisted
  // `is_selected` set to match the applicable set the client already computed
  // (excluding the still-checked-but-inapplicable action), the same set every
  // other sync path in this file (handler syncs, the passive-sync effect below)
  // already sends.
  const reconciledMissingEntityRef = useRef<string>('');
  // The applicable set THIS effect last told (or is telling) the server, as
  // opposed to `lastSyncedApplicableRef` below. That ref is initialised to the
  // MOUNT applicable set on the assumption "the server already reflects this" —
  // an assumption this effect exists specifically because it's false for a
  // checked `missing_entity` action. Reusing it here would make `needsSync`
  // compare `applicableIdsRef.current` against a value that, at mount and again
  // immediately after a failed retry, is ALWAYS identical to it (nothing has
  // synced yet), permanently short-circuiting the very PATCH this effect exists
  // to send. `null` means "not yet confirmed synced by this effect" — true at
  // mount and again after a failed PATCH — so the guard only skips a redundant
  // PATCH once an earlier reconciliation in this session has actually run.
  const lastReconciledApplicableRef = useRef<number[] | null>(null);
  // The proposal `lastReconciledApplicableRef` was last confirmed against. A
  // reused provider that switches proposals keeps these refs, so without this
  // reset a new proposal whose applicable set happens to equal the previous
  // one's would satisfy the `needsSync` guard below and skip the reconciliation
  // PATCH — leaving the new proposal's checked `missing_entity` action
  // unreconciled and approval stuck at 409 STALE_SELECTION with no user action.
  // (`reconciledMissingEntityRef`'s signature is already keyed by `proposalId`;
  // this ref is not, so it must be reset explicitly.)
  const reconciledProposalIdRef = useRef(proposalId);
  useEffect(() => {
    if (reconciledProposalIdRef.current !== proposalId) {
      reconciledProposalIdRef.current = proposalId;
      lastReconciledApplicableRef.current = null;
    }
    const selectedMissingEntityIds = selectedActionIds.filter(
      id => actionsById.get(id)?.verdict?.status === 'missing_entity'
    );
    if (selectedMissingEntityIds.length === 0) {
      reconciledMissingEntityRef.current = '';
      return;
    }
    const signature = `${proposalId}:${[...selectedMissingEntityIds]
      .sort((a, b) => a - b)
      .join(',')}`;
    if (reconciledMissingEntityRef.current === signature) {
      return;
    }
    reconciledMissingEntityRef.current = signature;
    // Only sync when the applicable set actually differs from what THIS effect
    // already told the server — otherwise this fires an avoidable PATCH every
    // time the signature changes, even when an earlier reconciliation already
    // persisted the same set (e.g. the missing-entity id set shrank and grew
    // back to one that resolves to the same applicable ids).
    const needsSync =
      lastReconciledApplicableRef.current === null ||
      !sameIdSet(applicableIdsRef.current, lastReconciledApplicableRef.current);
    if (needsSync) {
      lastReconciledApplicableRef.current = applicableIdsRef.current;
      scheduleSync(
        () => applicableIdsRef.current,
        () => {
          // The reconciliation PATCH failed. Clear both markers so the refresh
          // that the failed sync triggers re-runs this effect and retries —
          // otherwise a stale `is_selected` set persists and approval stays
          // stuck at 409 STALE_SELECTION until an unrelated selection change.
          reconciledMissingEntityRef.current = '';
          lastReconciledApplicableRef.current = null;
        }
      );
    }
  }, [
    selectedActionIds,
    actionsById,
    proposalId,
    scheduleSync,
    applicableIdsRef,
  ]);
}
